import {
  CustomFieldProps,
  DateFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  TextareaFieldProps,
} from '@/components/form-builder/form-builder.types';
import { ReportsRepository } from '@/store/reports-state/reports-state.repository';
import { map, Observable, of } from 'rxjs';
import { DacDetailsComponent } from '../create-reports/dac-details/dac-details.component';
import { DemandeReportsComponent } from '../create-reports/demande-reports/demande-reports.component';

interface CreateReportsFormObjectProps {
  store: ReportsRepository;
  dacOptions: Observable<SelectOption[]>;
  demandeReports$: Observable<any[]>;
  showDemandeReports$: Observable<boolean>;
}

export const getCreateReportsFormObject = ({ store, dacOptions, demandeReports$, showDemandeReports$ }: CreateReportsFormObjectProps): DynamicForm => {
  // Stable observables for visibility
  const stableDateReportValue = store.getNestedObservable<Date>('createRequest', 'dateReport');
  const stableMotifReportValue = store.getNestedObservable<string>('createRequest', 'motifReport');
  const stableSourceDemandeValue = store.getNestedObservable<string>('createRequest', 'sourceDemande');
  
  const selectedDac$ = store.getSelectedDacDetails();
  const isDacNotSelected$ = selectedDac$.pipe(map(dac => !dac));

  const dacField: DynamicField<SelectFieldProps> = {
    label: 'clarification.reports.form.dac',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'dacId'),
    props: {
      placeholder: 'clarification.reports.form.dac_placeholder',
      options: dacOptions,
      value: store.getObservable<SelectOption>('dacSelected'),
      filterEnabled: true,
      onSelectChange: (option: SelectOption) => {
        store.setNested('createRequest', 'dacId', option.code);
        store.set('dacSelected', option);
        store.clearError('dacId');
      },
    },
  };

  const dacDetailsField: DynamicField<CustomFieldProps> = {
    variant: FieldVariant.CUSTOM,
    isHidden: isDacNotSelected$,
    props: {
      component: DacDetailsComponent,
      dacDetails$: selectedDac$,
    },
  };

  const dateReportField: DynamicField<DateFieldProps> = {
    label: 'clarification.reports.form.date',
    variant: FieldVariant.DATE,
    isRequired: true,
    class: 'w-full',
    error: store.getNestedObservable<string>('errors', 'dateReport'),
    props: {
      placeholder: 'Sélectionner une date',
      showTime: true,
      minDate: new Date(),
      value: stableDateReportValue,
      onChange: (val: Date) => {
        store.setNested('createRequest', 'dateReport', val);
        store.clearError('dateReport');
      },
    },
  };

  const motifReportField: DynamicField<TextareaFieldProps> = {
    label: 'clarification.reports.form.motif',
    variant: FieldVariant.TEXTAREA,
    isRequired: true,
    class: 'w-full',
    error: store.getNestedObservable<string>('errors', 'motifReport'),
    props: {
      placeholder: 'Motif du report',
      value: stableMotifReportValue,
      onChange: (val: string) => {
        store.setNested('createRequest', 'motifReport', val);
        store.clearError('motifReport');
      },
      rows: 5,
    },
  };

  const sourceDemandeField: DynamicField<SelectFieldProps> = {
    label: 'clarification.reports.form.source',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'sourceDemande'),
    props: {
      placeholder: 'clarification.reports.form.source_placeholder',
      options: of([
        { name: 'clarification.reports.form.source_options.DEMANDE_AC', code: 'DEMANDE_AC' },
        { name: 'clarification.reports.form.source_options.DEMANDE_CANDIDATS', code: 'DEMANDE_CANDIDATS' },
      ]),
      value: stableSourceDemandeValue.pipe(
        map(val => (val ? { name: `clarification.reports.form.source_options.${val}`, code: val } : undefined))
      ),
      onSelectChange: (option: SelectOption) => {
        store.setNested('createRequest', 'sourceDemande', option.code);
        store.clearError('sourceDemande');
      },
    },
  };

  const demandeReportsField: DynamicField<CustomFieldProps> = {
    variant: FieldVariant.CUSTOM,
    isHidden: showDemandeReports$.pipe(map(show => !show)),
    props: {
      component: DemandeReportsComponent,
      demandeReports$: demandeReports$,
    },
  };

  return {
    title: 'clarification.reports.form.title',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          { fields: [dacField] },
          { fields: [dacDetailsField] },
          { fields: [sourceDemandeField] },
          { fields: [dateReportField] },
          { fields: [motifReportField] },
          { fields: [demandeReportsField] },
        ],
      },
    ],
  };
};
